import React, { useCallback, useEffect, useState } from 'react';
import { EmblaCarouselType } from 'embla-carousel';
import { Box } from '../Box';
import Chip from '../Chip';
import { ChevronLeftIcon, ChevronRightIcon } from '../Icons';
import { useTheme } from '../../hooks';

export interface UsePrevNextButtonsType {
    prevBtnDisabled: boolean;
    nextBtnDisabled: boolean;
    onPrevButtonClick: () => void;
    onNextButtonClick: () => void;
}

export const usePrevNextButtons = (emblaApi: EmblaCarouselType | undefined): UsePrevNextButtonsType => {
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

    const onPrevButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollPrev();
    }, [emblaApi]);

    const onNextButtonClick = useCallback(() => {
        if (!emblaApi) return;
        emblaApi.scrollNext();
    }, [emblaApi]);

    const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
        setPrevBtnDisabled(!emblaApi.canScrollPrev());
        setNextBtnDisabled(!emblaApi.canScrollNext());
    }, []);

    useEffect(() => {
        if (!emblaApi) return;

        onSelect(emblaApi);
        emblaApi.on('reInit', onSelect).on('select', onSelect);
    }, [emblaApi, onSelect]);

    return {
        prevBtnDisabled,
        nextBtnDisabled,
        onPrevButtonClick,
        onNextButtonClick
    };
};

export interface CarouselArrowButtonProps {
    direction: 'prev' | 'next';
    disabled: boolean;
    onClick: () => void;
}

export const CarouselArrowButton = ({ direction, disabled, onClick }: CarouselArrowButtonProps) => {
    const theme = useTheme();

    const icon = direction === 'prev' ? <ChevronLeftIcon /> : <ChevronRightIcon />;

    return (
        <Box sx={{ alignItems: 'center', display: 'flex', height: 'fit-content', pointerEvents: 'auto' }}>
            <Chip
                color="secondary"
                data-testid={`click-${direction}`}
                disabled={disabled}
                skipFocusWhenDisabled
                icon={icon}
                onClick={onClick}
                sx={theme.mixins.scrollChip}
            />
        </Box>
    );
};
